import React, { useState, useEffect } from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { authService } from '../api/axios';
import {
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Link as MuiLink
} from '@mui/material';

function ResetPassword() {
  const location = useLocation();
  const [token, setToken] = useState('');
  const [currentPassword, setCurrentPassword] = useState(''); // NEW state for current password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true); // New state for initial loading

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const resetToken = queryParams.get('token');
    if (resetToken) {
      setToken(resetToken);
      setIsLoadingInitial(false); // Token found, stop initial loading
    } else {
      setError('Password reset token is missing from the URL.');
      setIsLoadingInitial(false); // No token, stop initial loading and show error
    }
  }, [location.search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setError('');

    if (!token) {
      setError('Cannot reset password without a valid token.');
      return;
    }
    if (!currentPassword) {
      setError('Please enter your current password.');
      return;
    }

    if (!newPassword || !confirmPassword) {
      setError('Please fill in both new password fields.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }

    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters long.');
      return;
    }

    // NEW: Prevent current password from being the same as new password
    if (currentPassword === newPassword) {
      setError('New password cannot be the same as the current password.');
      return;
    }


    setIsSubmitting(true);
    try {
      const response = await authService.post('/auth/reset-password', {
        token,
        currentPassword,
        newPassword
      });
      setMessage(response.data);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setToken('');
    } catch (err) {
      console.error('Password reset failed:', err.response || err);
      setError(`Password reset failed: ${err.response?.data || err.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Show loading spinner only during initial token check if a token might be present
  if (isLoadingInitial && location.search.includes('token=')) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress sx={{ mb: 2 }} />
        <Typography variant="h6">Loading password reset page...</Typography>
      </Box>
    );
  }

  return (
    <Box className="form-card">
      <Typography variant="h4" component="h1" gutterBottom sx={{ textAlign: 'center' }}>
        Reset Your Password
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {message && <Alert severity="success" sx={{ mb: 2 }}>{message}</Alert>}

      {(!message && token && !error) && ( // Show form if no success message, token is present, and no initial error
        <form onSubmit={handleSubmit}>
          {/* NEW: Current Password Field */}
          <TextField
            fullWidth
            margin="normal"
            label="Current Password"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
          <TextField
            fullWidth
            margin="normal"
            label="New Password"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            helperText="Minimum 8 characters"
          />
          <TextField
            fullWidth
            margin="normal"
            label="Confirm New Password"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ mt: 3, mb: 2 }}
            disabled={isSubmitting}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Reset Password'}
          </Button>
        </form>
      )}

      {((message && !error) || (!token && !isLoadingInitial)) && ( // Show login link after success or if no token initially (and not loading)
        <Typography variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
          Go to <MuiLink component={RouterLink} to="/login" sx={{ textTransform: 'none' }}>Login Page</MuiLink>
        </Typography>
      )}
    </Box>
  );
}

export default ResetPassword;
